import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Edit3, 
  Save, 
  Clock, 
  Upload, 
  Eye, 
  X, 
  Image as ImageIcon 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = () => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export default function AdmissionsHeroForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Admissions',
    title: 'Admissions',
    subtitle: 'Begin your admissions journey with clear steps, supportive guidance, and a welcoming school community.',
    slides: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    subtitle: false
  });

  const [pendingSlides, setPendingSlides] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);

  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  const [isAddingSlide, setIsAddingSlide] = useState(false);
  const [modalSlideData, setModalSlideData] = useState(null);

  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/admissions/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AdmissionsHeroForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str) return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) return mediaMap[str];
    if (mediaMap[mediaId]) return mediaMap[mediaId];

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let heroSection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/admissions/cms/sections`,
        { 
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          heroSection = result.data.find(s => s.section_type === 'admissions-hero');
        }
      }
      
      if (!heroSection) {
        const publicResponse = await fetch(
          `${apiBase}/admissions/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: getAuthHeaders()
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            heroSection = result.data.find(s => s.section_type === 'admissions-hero');
          }
        }
      }
      
      if (!heroSection) {
        throw new Error('Admissions hero section not found');
      }

      const secId = heroSection.id;
      setSectionId(secId);

      let pendingFieldsMap = { kicker: false, title: false, subtitle: false };
      let pendingSlidesMap = {};
      
      let published = heroSection.content_published || heroSection.content || {};
      if (typeof published === 'string') {
        try {
          published = JSON.parse(published);
        } catch (e) {
          published = {};
        }
      }

      const publishedEn = published.en || published || {};
      const transformedSlides = Array.isArray(publishedEn.slides) 
        ? publishedEn.slides.slice(0, 3).map((slideData, idx) => {
            if (typeof slideData === 'object' && slideData !== null) {
              const imageId = slideData.image !== undefined ? slideData.image : (slideData.slide || '');
              return {
                image: imageId || '',
                order: slideData.order || idx + 1
              };
            } else if (slideData) {
              return {
                image: String(slideData).trim(),
                order: idx + 1
              };
            } else {
              return {
                image: '',
                order: idx + 1
              };
            }
          })
        : [];
      
      const initialData = {
        kicker: publishedEn.kicker || publishedEn.badge || 'Admissions',
        title: publishedEn.title || 'Admissions',
        subtitle: publishedEn.subtitle || publishedEn.summary || 'Begin your admissions journey with clear steps, supportive guidance, and a welcoming school community.',
        slides: transformedSlides
      };

      try {
        const pendingResponse = await fetch(
          `${apiBase}/cms/section/${secId}/with-pending`,
          { 
            credentials: 'include',
            headers: getAuthHeaders()
          }
        );
        
        if (pendingResponse.ok) {
          const pendingResult = await pendingResponse.json();
          if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
            const { pending_changes } = pendingResult.data;
            if (Array.isArray(pending_changes)) {
              pending_changes.forEach(change => {
                if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                  let val = change.new_value;
                  if (typeof val === 'string') {
                    try { val = JSON.parse(val); } catch (e) {}
                  }

                  if (['kicker', 'title', 'subtitle', 'summary'].includes(change.field_id)) {
                    const mappedKey = change.field_id === 'summary' ? 'subtitle' : change.field_id;
                    pendingFieldsMap[mappedKey] = true;
                    if (typeof val === 'string') {
                      initialData[mappedKey] = val;
                    }
                  } else if (change.field_id && change.field_id.includes('slides.')) {
                    const match = change.field_id.match(/slides\.(\d+)\.(add|update|delete|image)/);
                    if (match) {
                      const slideIdx = parseInt(match[1], 10);
                      const op = match[2];
                      pendingSlidesMap[`slide_${slideIdx}`] = true;
                      if (op === 'delete') {
                        pendingSlidesMap[`slide_${slideIdx}_delete`] = true;
                      } else if (op === 'add') {
                        pendingSlidesMap[`slide_${slideIdx}_add`] = true;
                        if (val && !initialData.slides[slideIdx]) {
                          initialData.slides.push(typeof val === 'object' ? val : { image: String(val), order: slideIdx + 1 });
                        }
                      } else if (op === 'update' || op === 'image') {
                        if (val && initialData.slides[slideIdx]) {
                          if (typeof val === 'object') {
                            initialData.slides[slideIdx] = { ...initialData.slides[slideIdx], ...val };
                          } else {
                            initialData.slides[slideIdx].image = String(val);
                          }
                        }
                      }
                    }
                  }
                }
              });
            }
          }
        }
      } catch (e) {
        console.warn('[AdmissionsHeroForm] Error parsing pending changes:', e);
      }

      setFormData(initialData);
      setOriginalData(JSON.parse(JSON.stringify(initialData)));
      setPendingFields(pendingFieldsMap);
      setPendingSlides(pendingSlidesMap);
    } catch (err) {
      console.error('[AdmissionsHeroForm] Error loading section:', err);
      toast.error('Failed to load hero section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleOpenEditSlide = (slideIndex) => {
    setIsAddingSlide(false);
    setEditingSlideIndex(slideIndex);
    setModalSlideData({ ...formData.slides[slideIndex] });
  };

  const handleCloseModal = () => {
    setIsAddingSlide(false);
    setEditingSlideIndex(null);
    setModalSlideData(null);
    setImageUploading(false);
  };

  // Upload or Replace Slide Image (Only Replace, No Remove Image)
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are supported');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);
      formDataToUpload.append('section_type', 'admissions-hero');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setImageProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id || response.filename || response.url;
              const mediaUrl = response.url || response.file_path || (response.filename ? `/api/uploads/admissions/${response.filename}` : '');
              const origin = (apiBase || '').replace(/\/api\/?$/, '');
              const fullUrl = mediaUrl 
                ? (mediaUrl.startsWith('http') ? mediaUrl : `${origin}${mediaUrl}`)
                : String(mediaId);

              if (mediaId) {
                setModalSlideData(prev => ({
                  ...prev,
                  image: fullUrl || String(mediaId)
                }));

                setMediaMap(prev => ({
                  ...prev,
                  [mediaId]: fullUrl,
                  [String(mediaId)]: fullUrl
                }));

                toast.success('Slide photo uploaded successfully');
              } else {
                toast.error('No media reference returned from server');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response during image upload');
          }
        } else {
          toast.error('Upload failed with status ' + xhr.status);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Network error during image upload');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/admissions/cms/media/upload?section_type=admissions-hero`);
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  // Submit Single Overview Field (Kicker, Title, Subtitle)
  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const value = (formData[fieldName] || '').trim();
    if (!value) {
      toast.warning(`Please enter ${fieldName}`);
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    const labelMap = {
      kicker: 'Section Tag / Kicker',
      title: 'Headline Title',
      subtitle: 'Welcome Description'
    };

    try {
      const requestBody = {
        section_id: sectionId,
        field_id: fieldName,
        field_name: `Admissions Hero ${labelMap[fieldName] || fieldName}`,
        new_value: formData[fieldName],
        old_value: originalData[fieldName] || '',
        locale: 'en'
      };

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`${labelMap[fieldName] || fieldName} proposed for Principal approval`);
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));
        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.warning(`A pending proposal for ${labelMap[fieldName] || fieldName} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting field: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Submit Slide (Add or Update)
  const handleSaveSlideModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (!modalSlideData || !modalSlideData.image) {
      toast.warning('Please upload a slide image first');
      return;
    }

    const slideIndex = editingSlideIndex;
    const isNew = isAddingSlide;
    const op = isNew ? 'add' : 'update';
    const submitKey = isNew ? 'add_slide' : `slide_${slideIndex}`;
    setSubmitting(prev => ({ ...prev, [submitKey]: true }));

    const fieldId = `slides.${slideIndex}.${op}`;
    const fieldName = `${isNew ? 'Add' : 'Update'} Admissions Hero Slide ${slideIndex + 1}`;
    const oldValue = isNew ? null : (originalData.slides[slideIndex] || {});

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: modalSlideData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Slide ${slideIndex + 1} proposed for Principal approval`);
        setPendingSlides(prev => ({
          ...prev,
          [`slide_${slideIndex}`]: true,
          [`slide_${slideIndex}_add`]: isNew
        }));

        if (isNew) {
          setFormData(prev => ({
            ...prev,
            slides: [...prev.slides, { ...modalSlideData }]
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            slides: prev.slides.map((s, i) => i === slideIndex ? { ...modalSlideData } : s)
          }));
        }

        handleCloseModal();
      } else {
        if (response.status === 409) {
          toast.warning(`A pending proposal for Slide ${slideIndex + 1} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting slide: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [submitKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-form-container">
        <div className="unified-loading-container">
          <div className="unified-loading-spinner" />
          <p className="unified-loading-text">Loading admissions hero configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Sparkles size={22} />
          </div>
          <div>
            <h2 className="unified-title">Admissions - Hero Banner</h2>
            <p className="unified-subtitle">
              Configure admissions portal headline, welcome description, and carousel slides
            </p>
          </div>
        </div>
      </div>

      {/* Hero Kicker Card */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={submitting.kicker || pendingFields.kicker}
            placeholder="e.g. Admissions or ENROLLMENT & ADMISSIONS"
            maxLength={60}
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} />
            {submitting.kicker ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Hero Title Card */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Headline Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Admissions"
            maxLength={100}
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} />
            {submitting.title ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Hero Subtitle Card */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Welcome Description / Subtitle</label>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={submitting.subtitle || pendingFields.subtitle}
            placeholder="e.g. Begin your admissions journey with clear steps, supportive guidance, and a welcoming school community."
            maxLength={500}
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} />
            {submitting.subtitle ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Slides Card */}
      <div className="unified-field-card">
        <div className="unified-field-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <label className="unified-label">Hero Carousel Slides (3 Slides)</label>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
              Visual banner background photos rotated across the admissions hero header (Replace Image Only)
            </p>
          </div>
        </div>

        {formData.slides.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <ImageIcon size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No hero slides configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Slide" above to upload a carousel photo</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginTop: '12px' }}>
            {formData.slides.map((slide, idx) => {
              let safeSlide = '';
              if (typeof slide === 'object' && slide !== null) {
                safeSlide = slide.image || slide.slide || '';
              } else if (typeof slide === 'string' || typeof slide === 'number') {
                safeSlide = String(slide);
              }
              const isPending = pendingSlides[`slide_${idx}`];
              const resolvedUrl = getMediaUrl(safeSlide);
              const isDeleteSubmitting = submitting[`delete_slide_${idx}`];

              return (
                <div
                  key={idx}
                  style={{
                    border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                    borderRadius: '12px',
                    background: isPending ? '#fffdf7' : '#ffffff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {/* Thumbnail with Lightbox */}
                  <div
                    style={{
                      height: '150px',
                      background: '#0f172a',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {resolvedUrl ? (
                      <>
                        <img
                          src={resolvedUrl}
                          alt={`Slide ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => setLightboxImage(resolvedUrl)}
                        />
                        <button
                          type="button"
                          onClick={() => setLightboxImage(resolvedUrl)}
                          style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '11px',
                            gap: '4px'
                          }}
                          title="View Full Photo"
                        >
                          <Eye size={12} />
                        </button>
                      </>
                    ) : (
                      <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <ImageIcon size={28} />
                        <span style={{ fontSize: '12px' }}>No photo selected</span>
                      </div>
                    )}
                  </div>

                  {/* Info & Actions */}
                  <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 800, fontSize: '14px', color: '#0f172a' }}>
                        Slide {idx + 1}
                      </span>
                      {isPending && (
                        <span 
                          className="unified-pending-pill"
                          style={pendingSlides[`slide_${idx}_delete`] ? { background: '#fef2f2', color: '#dc2626', borderColor: '#fca5a5' } : {}}
                        >
                          <Clock size={11} /> {pendingSlides[`slide_${idx}_delete`] ? 'Pending Deletion' : pendingSlides[`slide_${idx}_add`] ? 'Pending Addition' : 'Pending Review'}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => handleOpenEditSlide(idx)}
                        disabled={isPending || isDeleteSubmitting}
                        style={{ flex: 1, justifyContent: 'center' }}
                      >
                        <Edit3 size={13} /> Change Photo
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Slide Add/Edit Modal */}
      {editingSlideIndex !== null && modalSlideData && (
        <div className="unified-modal-overlay" onClick={handleCloseModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Sparkles size={20} color="#2563eb" />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  {isAddingSlide ? 'Add Hero Slide' : `Edit Hero Slide ${editingSlideIndex + 1}`}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Image Preview */}
              <div
                style={{
                  width: '100%',
                  height: '220px',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  background: '#f8fafc',
                  border: '1.5px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                {modalSlideData.image && getMediaUrl(modalSlideData.image) ? (
                  <img
                    src={getMediaUrl(modalSlideData.image)}
                    alt="Slide preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                    <ImageIcon size={36} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: '13px' }}>No photo selected yet</p>
                  </div>
                )}
              </div>

              {/* Upload Input (Replace only, no remove image) */}
              <div>
                <input
                  type="file"
                  id={`admissions-hero-slide-input-${editingSlideIndex}`}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageUpload}
                  disabled={imageUploading}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor={`admissions-hero-slide-input-${editingSlideIndex}`}
                  className="unified-secondary-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 18px',
                    cursor: imageUploading ? 'not-allowed' : 'pointer',
                    width: '100%'
                  }}
                >
                  <Upload size={16} />
                  {imageUploading
                    ? `Uploading (${imageProgress}%)...`
                    : modalSlideData.image
                    ? 'Replace Slide Photo'
                    : 'Choose Image from Device'}
                </label>
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                  Max 5MB • Recommended: 1600×900px • JPG, PNG, WebP
                </p>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                disabled={
                  submitting[isAddingSlide ? 'add_slide' : `slide_${editingSlideIndex}`] ||
                  !modalSlideData.image ||
                  imageUploading
                }
                onClick={handleSaveSlideModal}
              >
                <Save size={14} />
                {submitting[isAddingSlide ? 'add_slide' : `slide_${editingSlideIndex}`]
                  ? 'Submitting...'
                  : isAddingSlide
                  ? 'Propose New Slide'
                  : 'Submit Slide Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#000000',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0,0,0,0.7)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImage}
              alt="Full Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', display: 'block', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
